import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataProvider } from 'react-admin';

export type EnrollableCourse = {
    id: number;
    title: string;
    enrollmentMode?: string;
    isLicensedReference?: boolean;
};

type Severity = 'success' | 'info' | 'error';

interface Options {
    notify: (message: string, severity: Severity) => void;
    onEnrolled?: (courseId: number, enrollment: unknown) => void;
}

/**
 * Gemeinsame Einschreibe-Logik für Kurskatalog und Empfehlungs-Widget (#318).
 * Spiegelt CourseCatalog.handleEnroll: self -> POST + /my-courses,
 * request -> Anfrage-Hinweis ohne Navigate, lizenzierte Referenz -> eigener Endpoint.
 */
export function useCourseEnrollment({ notify, onEnrolled }: Options) {
    const navigate = useNavigate();
    const dataProvider = useDataProvider();
    const [enrollingCourseId, setEnrollingCourseId] = useState<number | null>(null);

    const enroll = async (course: EnrollableCourse) => {
        setEnrollingCourseId(course.id);
        try {
            if (course.isLicensedReference) {
                const token = localStorage.getItem('token');
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/licensed-courses/${course.id}/enroll`,
                    {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                    }
                );
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || errorData.message || 'Einschreibung fehlgeschlagen');
                }
                notify(`Erfolgreich in "${course.title}" eingeschrieben!`, 'success');
                onEnrolled?.(course.id, null);
                setTimeout(() => navigate('/my-courses'), 1500);
            } else {
                const result = await dataProvider.create('course_enrollments', {
                    data: {
                        course: `/api/courses/${course.id}`,
                        status: 'enrolled',
                    }
                });
                onEnrolled?.(course.id, result.data);
                if (result.data?.status === 'pending_approval') {
                    notify('Ihre Einschreibungsanfrage wurde gesendet. Sie werden benachrichtigt, sobald sie genehmigt wird.', 'info');
                } else {
                    notify(`Erfolgreich in "${course.title}" eingeschrieben!`, 'success');
                    setTimeout(() => navigate('/my-courses'), 1500);
                }
            }
        } catch (error: any) {
            const detail = error?.body?.['hydra:description'] || error?.body?.detail || error?.message || '';
            const d = detail.toLowerCase();
            let msg: string;
            if (d.includes('already enrolled')) {
                msg = 'Sie sind bereits in diesem Kurs eingeschrieben.';
            } else if (d.includes('self-enrollment is not allowed')) {
                msg = 'Selbsteinschreibung ist für diesen Kurs nicht aktiviert.';
            } else if (d.includes('must complete')) {
                msg = 'Voraussetzungen nicht erfüllt. Bitte schließen Sie zuerst die erforderlichen Kurse ab.';
            } else if (d.includes('cannot enroll') || d.includes('no valid license')) {
                msg = 'Einschreibung nicht möglich: Keine gültige Lizenz für diesen Kurs.';
            } else if (d.includes('pending')) {
                msg = 'Es liegt bereits eine ausstehende Einschreibungsanfrage für diesen Kurs vor.';
            } else {
                msg = detail || 'Fehler beim Einschreiben. Bitte versuchen Sie es erneut.';
            }
            notify(msg, 'error');
        } finally {
            setEnrollingCourseId(null);
        }
    };

    return { enroll, enrollingCourseId };
}
